import React, { useState, useEffect } from 'react';
import axiosInstance from "../config/axiosConfig";
import BuscarPaciente from "./components/BuscarPaciente";
import BuscarAuxiliar from "./components/BuscarAuxiliar";
import { formatDate } from "../utils/utils";
/* global gdAlertify */

const NuevoAcuerdoMutuo = () => {
    // Estados principales
    const [paciente, setPaciente] = useState(null);
    const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
    const [patternType, setPatternType] = useState(null); // 'fixed' | 'rotative'
    const [patternRows, setPatternRows] = useState([]); // Estructura normalizada del patrón

    // Estado para almacenar la configuración de cada "Slot" generado
    // Clave: índice del slot. Valor: { auxiliar: null, valores: {...} }
    const [slotConfigs, setSlotConfigs] = useState({});

    // Valores por defecto para copiar/pegar
    const [defaultValues, setDefaultValues] = useState({
        valorTurno: "",
        valorMovilizacion: "",
        valorColacion: "",
        valorOtro: ""
    });

    // --- 1. Manejo de Selección de Paciente ---
    const handleSelectPaciente = async (p) => {
        if (!p) {
            setPaciente(null);
            setPatternRows([]);
            return;
        }

        gdAlertify.showLoader();
        try {
            // Buscamos detalles frescos del paciente para obtener el turno/patrón
            const response = await axiosInstance.get("/paciente/busqueda", { params: { id: p.id } });

            if (response.data && !response.data.data) {
                gdAlertify.notiError("Error consultando paciente");
                return;
            }

            const fullData = response.data.data[0] || {};
            setPaciente(fullData);
            parsePattern(fullData.turnos);

            // Pre-cargar valores base
            if (fullData.valoresServicio && fullData.valoresServicio.length > 0) {
                const vs = fullData.valoresServicio[0];
                setDefaultValues({
                    valorTurno: vs.valorTurnoAuxiliar || 0,
                    valorMovilizacion: vs.valorLocomocionAuxiliar || 0,
                    valorColacion: vs.valorColacionAuxiliar || 0,
                    tipoColacion: "3", // Default Ambos
                    valorOtro: vs.valorOtroAuxiliar || 0
                });
            }

            // Intentar cargar acuerdos vigentes (simulado o consulta real si existiera endpoint filtrado)
            // Aquí idealmente llamaríamos a /acuerdo-mutuo/vigentes?pacienteId=p.id
            // Como no tenemos el endpoint confirmado, dejaremos un espacio visual para ello en el slot si corresponde.

        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error de conexión");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    // --- 2. Lógica de Parseo de Patrón (tomada de EditarPaciente.js) ---
    const parsePattern = (turno) => {
        if (!turno) {
            setPatternRows([]);
            return;
        }

        const type = turno.type === "rotative" ? "rotative" : "fixed";
        setPatternType(type);

        let parsed = [];

        if (type === "rotative") {
            try {
                if (typeof turno.rotation_pattern === "string") {
                    const raw = JSON.parse(turno.rotation_pattern);
                    parsed = Array.isArray(raw) ? raw : [raw];
                } else if (typeof turno.rotation_pattern === "object" && turno.rotation_pattern !== null) {
                    parsed = [turno.rotation_pattern];
                }

                // Normalizamos para asegurar que todos tengan los campos necesarios
                parsed = parsed.map(p => ({
                    days: parseInt(p.days) || 1,
                    period: p.period || 'day',
                    status: p.status || 'libre'
                }));

            } catch (e) {
                console.error("Error parseando rotacion", e);
            }
        } else {
            // Patrón Fijo: Convertimos los días de servicio (Mon, Tue...) a una estructura visual
            // Esto es una simplificación. Para "Fijo", normalmente se asignan días específicos.
            // Para mantener la consistencia visual de "Slots", crearemos un slot por cada día de la semana.
            const daysMap = { 'Mon': 'Lunes', 'Tue': 'Martes', 'Wed': 'Miércoles', 'Thu': 'Jueves', 'Fri': 'Viernes', 'Sat': 'Sábado', 'Sun': 'Domingo' };
            const serviceDays = turno.days_service ? turno.days_service.split(",") : [];

            // Generamos 7 slots representando la semana tipo
            Object.keys(daysMap).forEach((key) => {
                if (serviceDays.includes(key)) {
                    parsed.push({
                        label: daysMap[key],
                        isFixedDay: true,
                        period: turno.period || 'day',
                        status: 'worked',
                        dayCode: key
                    });
                }
            });
        }

        setPatternRows(parsed);
    };

    // --- 3. Cálculo de Fechas Dinámicas ---
    const calculateSlotDateInfo = (index, row) => {
        if (!fechaInicio) return { label: "Definir fecha inicio", start: null, end: null };

        if (patternType === 'fixed') {
            return { label: `${row.label} (Recurrente)`, start: null, end: null };
        }

        let currentDate = new Date(fechaInicio);

        // Sumamos los días de los slots anteriores
        for (let i = 0; i < index; i++) {
            currentDate.setDate(currentDate.getDate() + (parseInt(patternRows[i].days) || 0));
        }

        const startDateStr = formatDate(currentDate.toISOString().split('T')[0]);
        const dayOfWeek = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(currentDate);

        // Fecha fin
        let endDate = new Date(currentDate);
        // Restamos 1 porque si dura 1 día, empieza y termina el mismo día
        const daysDuration = (parseInt(row.days) || 1);
        endDate.setDate(endDate.getDate() + Math.max(0, daysDuration - 1));

        return {
            label: `${dayOfWeek} ${startDateStr}`,
            startDateStr,
            fullLabel: `Inicio: ${dayOfWeek} ${startDateStr} (${daysDuration} días)`,
            start: currentDate,
            end: endDate
        };
    };

    // --- 4. Gestión de Estado de Slots (Edición de Patrón) ---
    // Modificar un campo de la estructura del SLOT (días, periodo, status)
    const handlePatternChange = (index, field, value) => {
        const newRows = [...patternRows];
        newRows[index] = { ...newRows[index], [field]: value };
        setPatternRows(newRows);

        // Si cambia a "worked", pre-cargar valores base si están vacíos
        if (field === 'status' && value === 'worked') {
            setSlotConfigs(prev => {
                const currentConfig = prev[index] || {};
                // Solo aplicar si no tiene valores ya definidos (para no sobrescribir ediciones manuales si el usuario juega con el estado)
                // O según requerimiento: "parten con el base". Si estaba libre, no tenía valores visibles.
                // Asumiremos que si estaba libre, hay que ponerle los defaults.
                if (!currentConfig.valorTurno) {
                    return {
                        ...prev,
                        [index]: {
                            ...currentConfig,
                            ...defaultValues
                        }
                    };
                }
                return prev;
            });
        }
    };

    // Agregar nuevo slot
    const handleAddSlot = () => {
        setPatternRows([...patternRows, { days: 1, period: 'day', status: 'libre' }]);
    };

    // Eliminar slot
    const handleRemoveSlot = (index) => {
        const newRows = patternRows.filter((_, i) => i !== index);
        setPatternRows(newRows);

        // Limpiar config asignada a ese índice (opcional, por limpieza)
        const newConfigs = { ...slotConfigs };
        delete newConfigs[index];
        setSlotConfigs(newConfigs);
    };

    // Gestión de Configuración (Auxiliar/Valores) del Slot
    const handleSlotConfigChange = (index, field, value) => {
        setSlotConfigs(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                [field]: value
            }
        }));
    };

    const handleApplyDefaults = (index) => {
        setSlotConfigs(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                ...defaultValues
            }
        }));
    };

    const generateWhatsAppLink = (slotConfig, row, dateInfo) => {
        if (!slotConfig?.auxiliar?.celular) return null;

        const nombreAux = slotConfig.auxiliar.nombre || "Auxiliar";

        let periodoLabel = 'Turno';
        if (row.period === 'day') periodoLabel = 'Día';
        else if (row.period === 'night') periodoLabel = 'Noche';
        else if (row.period === '24') periodoLabel = 'Día y Noche';

        const fechas = dateInfo ? dateInfo.label : "Fechas a confirmar";

        // Agregar horario si existe
        let horarioInfo = "";
        if (row.startTime || row.endTime) {
            horarioInfo = `\nHorario: ${row.startTime || '?'} a ${row.endTime || '?'}`;
        }

        const text = `Hola ${nombreAux}, te contacto para confirmar asignación: \nPaciente: *${paciente.nombres}*\nTurno: *${periodoLabel}*${horarioInfo}\nFecha Inicio: ${fechas} (${row.days} días)\nValor: $${slotConfig.valorTurno || 0}.\n\n¿Confirmas?`;

        return `https://wa.me/${slotConfig.auxiliar.celular.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
    };

    // --- 5. Guardado ---
    const handleGuardarTodo = () => {
        // ... (misma lógica de guardado, ahora patternRows es el editado)
        const acuerdosToSave = [];

        Object.keys(slotConfigs).forEach(key => {
            const config = slotConfigs[key];
            const patternRow = patternRows[key];

            // Guardamos solo si hay auxiliar asignado y el slot NO es libre
            if (config.auxiliar && patternRow && patternRow.status !== 'libre') {
                acuerdosToSave.push({
                    pacienteId: paciente.id,
                    auxiliarId: config.auxiliar.id,
                    valores: {
                        valorTurno: config.valorTurno,
                        valorMovilizacion: config.valorMovilizacion,
                        valorColacion: config.valorColacion,
                        valorOtro: config.valorOtro
                    },
                    patternInfo: {
                        type: patternType,
                        startDate: fechaInicio,
                        slotIndex: key,
                        period: patternRow.period,
                        days: patternRow.days
                    }
                });
            }
        });

        // También podríamos querer guardar el PATRÓN actualizado si el usuario lo modificó
        // pending logic: update patient pattern

        if (acuerdosToSave.length === 0) {
            gdAlertify.notiWarning("No hay asignaciones válidas para guardar.");
            return;
        }

        console.log("Guardando:", acuerdosToSave);
        gdAlertify.alertSuccess(`Procesando ${acuerdosToSave.length} acuerdos...`);
    };

    // Estado para modal de proyección
    const [showModal, setShowModal] = useState(false);

    // Generar proyección rotativa (Simulación de 2 meses aprox)
    const generateProjection = (totalDays = 42) => {
        if (!fechaInicio || patternRows.length === 0) return [];
        let items = [];
        let ptrDate = new Date(fechaInicio);
        let daysGenerated = 0;
        let pIndex = 0;

        // Evitar loop infinito si patrón está vacío o mal formado
        if (patternRows.every(r => (parseInt(r.days) || 0) <= 0)) return [];

        while (daysGenerated < totalDays) {
            const row = patternRows[pIndex % patternRows.length];
            const count = parseInt(row.days) || 1;

            for (let i = 0; i < count; i++) {
                if (daysGenerated >= totalDays) break;

                const itemDate = new Date(ptrDate);
                items.push({
                    date: itemDate,
                    type: row.period,
                    status: row.status,
                    slotIndex: (pIndex % patternRows.length) + 1,
                    originalRow: row
                });

                ptrDate.setDate(ptrDate.getDate() + 1);
                daysGenerated++;
            }
            pIndex++;
        }

        return items;
    };

    const projectionItems = showModal ? generateProjection(20) : [];

    // Helper para celdas vacías del calendario (offset inicial) - YA NO ES NECESARIO PERO NO MOLESTA
    const getStartOffset = () => {
        if (projectionItems.length === 0) return 0;
        const day = projectionItems[0].date.getDay();
        return day === 0 ? 6 : day - 1;
    };

    const startOffset = getStartOffset();

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow-sm">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Gestor de Acuerdos y Patrones</h4>
                    <button className="btn btn-light btn-sm fw-bold text-primary" onClick={() => setShowModal(true)}>
                        <i className="bi bi-list-check me-2"></i>
                        Ver Simulación
                    </button>
                </div>

                <div className="card-body">
                    {/* SECCIÓN 1: CABECERA */}
                    <div className="row mb-4 align-items-end">
                        <div className="col-md-5">
                            <label className="form-label fw-bold">Paciente</label>
                            <BuscarPaciente onSelect={handleSelectPaciente} />
                            {paciente && (
                                <div className="mt-2 small text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Al guardar una nueva planificación, <strong>se cerrarán automáticamente los acuerdos vigentes</strong> y comenzarán los nuevos a partir de la fecha indicada.
                                </div>
                            )}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Fecha Inicio Ciclo</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4 text-end">
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleAddSlot}>
                                <i className="bi bi-plus-lg me-1"></i> Agregar Nuevo Slot
                            </button>
                        </div>
                    </div>

                    {/* VALORES BASE GLOBALES */}
                    {paciente && (
                        <div className="card bg-light border-0 mb-4">
                            <div className="card-body py-3">
                                <h6 className="card-title text-muted text-uppercase small fw-bold mb-2">Valores Base para la Asignación</h6>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">Base Turno</span>
                                            <span className="input-group-text">$</span>
                                            <input type="number" className="form-control"
                                                value={defaultValues.valorTurno}
                                                onChange={(e) => setDefaultValues({ ...defaultValues, valorTurno: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">Colación</span>
                                            <span className="input-group-text">$</span>
                                            <input type="number" className="form-control"
                                                value={defaultValues.valorColacion}
                                                onChange={(e) => setDefaultValues({ ...defaultValues, valorColacion: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">Tipo</span>
                                            <select className="form-select"
                                                value={defaultValues.tipoColacion || "3"}
                                                onChange={(e) => setDefaultValues({ ...defaultValues, tipoColacion: e.target.value })}
                                            >
                                                <option value="1">Día</option>
                                                <option value="2">Noche</option>
                                                <option value="3">Ambos</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">Movilización</span>
                                            <span className="input-group-text">$</span>
                                            <input type="number" className="form-control"
                                                value={defaultValues.valorMovilizacion}
                                                onChange={(e) => setDefaultValues({ ...defaultValues, valorMovilizacion: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-1">
                                        {/* Optional: Button to apply to all empty slots if desired by user later */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <hr />

                    {/* SECCIÓN 2: EDITOR DE SLOTS */}
                    {paciente ? (
                        <div className="planificador-container">
                            <h5 className="mb-4 text-secondary">Planificación de Ciclo ({patternRows.length} Turnos)</h5>

                            <div className="d-flex flex-column gap-3">
                                {patternRows.map((row, index) => {
                                    const dateInfo = calculateSlotDateInfo(index, row);
                                    const isFree = row.status === 'libre';
                                    const slotConfig = slotConfigs[index] || {};

                                    return (
                                        <div key={index} className={`card ${isFree ? 'bg-light border-0' : 'border-primary'} shadow-sm`}>
                                            <div className="card-body p-0">
                                                <div className="row g-0">

                                                    {/* COLUMNA 1: ESTRUCTURA TEMPORAL */}
                                                    <div className="col-md-4 border-end bg-light-subtle rounded-start p-2 d-flex flex-column">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="badge bg-primary rounded-circle" style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>#{index + 1}</span>
                                                                <span className={`badge ${isFree ? 'bg-secondary' : 'bg-success'}`} style={{ fontSize: '0.7rem' }}>
                                                                    {isFree ? 'Libre' : 'Laborable'}
                                                                </span>
                                                            </div>
                                                            <button className="btn btn-sm text-danger hover-bg-danger-subtle rounded-circle p-0" onClick={() => handleRemoveSlot(index)} title="Eliminar Turno">
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>

                                                        <div className="mb-2">
                                                            <h6 className="fw-bold text-dark mb-0 small">{dateInfo.startDateStr}</h6>
                                                            <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{dateInfo.label.split(' ')[0]}</small>
                                                        </div>

                                                        {/* Inputs de Estructura siempre visibles */}
                                                        <div className="flex-grow-1">
                                                            <div className="row g-1 mb-2">
                                                                <div className="col-6">
                                                                    <label className="small text-muted fw-bold mb-0" style={{ fontSize: '0.7rem' }}>Duración</label>
                                                                    <div className="input-group input-group-sm">
                                                                        <input type="number" className="form-control px-1" min="1"
                                                                            value={row.days}
                                                                            onChange={(e) => handlePatternChange(index, 'days', e.target.value)}
                                                                        />
                                                                        <span className="input-group-text px-1" style={{ fontSize: '0.7rem' }}>Días</span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-6">
                                                                    <label className="small text-muted fw-bold mb-0" style={{ fontSize: '0.7rem' }}>Jornada</label>
                                                                    <select className="form-select form-select-sm px-1" style={{ fontSize: '0.8rem' }}
                                                                        value={row.period}
                                                                        onChange={(e) => handlePatternChange(index, 'period', e.target.value)}
                                                                    >
                                                                        <option value="day">Día</option>
                                                                        <option value="night">Noche</option>
                                                                        <option value="24">Día/Noche</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="row g-1">
                                                                <div className="col-6">
                                                                    <label className="small text-muted fw-bold mb-0" style={{ fontSize: '0.7rem' }}>Inicio</label>
                                                                    <input type="time" className="form-control form-control-sm px-1"
                                                                        value={row.startTime || ''}
                                                                        onChange={(e) => handlePatternChange(index, 'startTime', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <label className="small text-muted fw-bold mb-0" style={{ fontSize: '0.7rem' }}>Fin</label>
                                                                    <input type="time" className="form-control form-control-sm px-1"
                                                                        value={row.endTime || ''}
                                                                        onChange={(e) => handlePatternChange(index, 'endTime', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="mt-2 pt-2 border-top">
                                                            <div className="btn-group w-100 btn-group-sm" role="group">
                                                                <input type="radio" className="btn-check" name={`status-${index}`} id={`worked-${index}`} autoComplete="off"
                                                                    checked={!isFree}
                                                                    onChange={() => handlePatternChange(index, 'status', 'worked')}
                                                                />
                                                                <label className={`btn py-0 ${!isFree ? 'btn-outline-success' : 'btn-outline-secondary'}`} htmlFor={`worked-${index}`} style={{ fontSize: '0.75rem' }}>
                                                                    <i className="bi bi-briefcase me-1"></i>Trabajo
                                                                </label>

                                                                <input type="radio" className="btn-check" name={`status-${index}`} id={`free-${index}`} autoComplete="off"
                                                                    checked={isFree}
                                                                    onChange={() => handlePatternChange(index, 'status', 'libre')}
                                                                />
                                                                <label className={`btn py-0 ${isFree ? 'btn-outline-secondary active' : 'btn-outline-secondary'}`} htmlFor={`free-${index}`} style={{ fontSize: '0.75rem' }}>
                                                                    <i className="bi bi-cup-hot me-1"></i>Libre
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* COLUMNA 2: ASIGNACIÓN Y VALORES */}
                                                    <div className="col-md-8 p-2 bg-white rounded-end">
                                                        {!isFree ? (
                                                            <div className="d-flex flex-column h-100 justify-content-center">
                                                                <div className="mb-2">
                                                                    <label className="form-label small fw-bold text-uppercase text-primary mb-1" style={{ fontSize: '0.7rem' }}>
                                                                        <i className="bi bi-person-fill-check me-1"></i>
                                                                        Profesional
                                                                    </label>
                                                                    <div className="d-flex gap-2">
                                                                        <div className="flex-grow-1">
                                                                            <BuscarAuxiliar
                                                                                onSelect={(aux) => handleSlotConfigChange(index, 'auxiliar', aux)}
                                                                                defaultValue={slotConfig.auxiliar}
                                                                            />
                                                                        </div>
                                                                        {slotConfig.auxiliar && (
                                                                            <a
                                                                                href={generateWhatsAppLink(slotConfig, row, dateInfo)}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="btn btn-success d-flex align-items-center px-3 shadow-sm py-0"
                                                                                title="WhatsApp"
                                                                            >
                                                                                <i className="bi bi-whatsapp"></i>
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="bg-light p-2 rounded-2 border border-light-subtle">
                                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                                        <label className="small fw-bold text-uppercase text-secondary m-0" style={{ fontSize: '0.7rem' }}>
                                                                            <i className="bi bi-cash-coin me-1"></i> Valores
                                                                        </label>
                                                                        <button className="btn btn-sm btn-link text-decoration-none py-0 small" style={{ fontSize: '0.7rem' }} onClick={() => handleApplyDefaults(index)}>
                                                                            Usar Base
                                                                        </button>
                                                                    </div>
                                                                    <div className="row g-1">
                                                                        <div className="col-md-4">
                                                                            <div className="input-group input-group-sm shadow-sm">
                                                                                <span className="input-group-text bg-white border-end-0 text-muted px-1" style={{ fontSize: '0.7rem' }}>Turno</span>
                                                                                <input type="number" className="form-control border-start-0 ps-1" placeholder="0"
                                                                                    value={slotConfig.valorTurno || ''}
                                                                                    onChange={(e) => handleSlotConfigChange(index, 'valorTurno', e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <div className="input-group input-group-sm shadow-sm">
                                                                                <span className="input-group-text bg-white border-end-0 text-muted px-1" style={{ fontSize: '0.7rem' }}>Col.</span>
                                                                                <input type="number" className="form-control border-start-0 border-end-0 ps-1" placeholder="0"
                                                                                    value={slotConfig.valorColacion || ''}
                                                                                    onChange={(e) => handleSlotConfigChange(index, 'valorColacion', e.target.value)}
                                                                                />
                                                                                <select className="form-select border-start-0 bg-light px-0 ps-1" style={{ maxWidth: '75px', fontSize: '0.75rem' }}
                                                                                    value={slotConfig.tipoColacion || '3'}
                                                                                    onChange={(e) => handleSlotConfigChange(index, 'tipoColacion', e.target.value)}
                                                                                >
                                                                                    <option value="1">Día</option>
                                                                                    <option value="2">Noche</option>
                                                                                    <option value="3">Ambos</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <div className="input-group input-group-sm shadow-sm">
                                                                                <span className="input-group-text bg-white border-end-0 text-muted px-1" style={{ fontSize: '0.7rem' }}>Mov.</span>
                                                                                <input type="number" className="form-control border-start-0 ps-1" placeholder="0"
                                                                                    value={slotConfig.valorMovilizacion || ''}
                                                                                    onChange={(e) => handleSlotConfigChange(index, 'valorMovilizacion', e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted" style={{ opacity: 0.6 }}>
                                                                <i className="bi bi-cup-hot fs-3 mb-1 text-secondary"></i>
                                                                <h6 className="fw-bold text-uppercase small mb-0">Libre</h6>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-5 mb-5 p-4 bg-lighter rounded border">
                                <div className="alert alert-warning d-flex align-items-start mb-3">
                                    <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                                    <div>
                                        <h5 className="alert-heading h6 fw-bold">Importante:</h5>
                                        <p className="mb-0 small">
                                            Al guardar esta planificación, el sistema <strong>cerrará automáticamente todos los acuerdos vigentes</strong> asociados a este paciente que entren en conflicto con la nueva fecha de inicio.
                                            Se generarán nuevos registros de acuerdos mutuos para cada auxiliar asignado arriba.
                                        </p>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="fw-bold text-primary">Resumen:</span> {patternRows.length} bloques definidos.
                                    </div>
                                    <button className="btn btn-primary btn-lg shadow" onClick={handleGuardarTodo}>
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        Guardar Planificación, Cerrar Anteriores y Crear Nuevos
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-5 text-muted bg-light rounded mt-4 border border-dashed">
                            <h3>Gestión Centralizada</h3>
                            <p>Selecciona un paciente arriba para cargar o crear su estructura de turnos.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL SIMULACIÓN - TABLA */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-list-check me-2"></i>
                                    Simulación de Ciclo (Próximos 20 Días)
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th className="px-3 py-2 text-center" style={{ width: '20%' }}>Fecha</th>
                                                <th className="px-3 py-2 text-center" style={{ width: '15%' }}>Turno</th>
                                                <th className="px-3 py-2 text-center">Asignación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projectionItems.map((item, i) => {
                                                const slotConfig = slotConfigs[item.slotIndex - 1] || {};
                                                const auxiliarNombre = slotConfig.auxiliar ? (slotConfig.auxiliar.label || slotConfig.auxiliar.nombre) : null;
                                                const hasAux = !!auxiliarNombre;
                                                const isFree = item.status === 'libre';

                                                // Color Logic for Slots (if assigned and not free)
                                                // Using slotIndex to rotate colors
                                                const colorPalette = ['table-primary', 'table-success', 'table-warning', 'table-info'];
                                                const slotColorClass = isFree ? 'table-secondary' : colorPalette[(item.slotIndex - 1) % 4];

                                                // Define content for Day and Night rows
                                                const renderCellContent = (shiftType) => {
                                                    // shiftType: 'day' or 'night'
                                                    const isActive = !isFree && (item.type === '24' || item.type === shiftType);

                                                    if (!isActive) {
                                                        return (
                                                            <div className="text-muted small fw-bold text-center" style={{ opacity: 0.5 }}>
                                                                <i className="bi bi-dash-lg"></i>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className={`d-flex align-items-center justify-content-between px-2 py-1 rounded ${hasAux ? '' : 'text-muted'}`}>
                                                            {hasAux ? (
                                                                <span className="fw-bold text-dark">
                                                                    <i className="bi bi-person-fill me-2"></i>
                                                                    {auxiliarNombre}
                                                                </span>
                                                            ) : (
                                                                <span className="small fst-italic">
                                                                    Slot #{item.slotIndex} (Sin Asignar)
                                                                </span>
                                                            )}
                                                            {hasAux && <i className="bi bi-check-circle-fill text-success small"></i>}
                                                        </div>
                                                    );
                                                };

                                                const cellClass = (shiftType) => {
                                                    const isActive = !isFree && (item.type === '24' || item.type === shiftType);
                                                    if (!isActive) return "bg-light"; // Plomo/Grayish
                                                    if (!hasAux) return "table-secondary"; // Plomo explicitly requested for unassigned? "si no lleva auxilair... queda plomo"
                                                    return slotColorClass;
                                                };

                                                return (
                                                    <React.Fragment key={i}>
                                                        {/* ROW 1: DÍA */}
                                                        <tr>
                                                            <td className="align-middle text-center bg-white" rowSpan={2}>
                                                                <div className="fw-bold text-dark">{formatDate(item.date.toISOString().split('T')[0])}</div>
                                                            </td>
                                                            <td className="align-middle text-center bg-white">
                                                                <span className="badge bg-white border border-primary text-primary fw-bold px-3">
                                                                    DÍA
                                                                </span>
                                                            </td>
                                                            <td className={`align-middle ${cellClass('day')}`}>
                                                                {renderCellContent('day')}
                                                            </td>
                                                        </tr>
                                                        {/* ROW 2: NOCHE */}
                                                        <tr>
                                                            <td className="align-middle text-center bg-white">
                                                                <span className="badge bg-white border border-danger text-danger fw-bold px-2">
                                                                    NOCHE
                                                                </span>
                                                            </td>
                                                            <td className={`align-middle ${cellClass('night')}`}>
                                                                {renderCellContent('night')}
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer bg-light">
                                <div className="me-auto small text-muted">
                                    <i className="bi bi-arrow-repeat me-1"></i>
                                    El patrón se repite automáticamente.
                                </div>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default NuevoAcuerdoMutuo;
